/**
 * The page shell every settings page wears — interface I-3 (C11, FR-A8 §3),
 * built on Wave 14's `EditPage` (C13, ADR-0032 §5; interface I-1).
 *
 * `SettingsPage({title, description, children, actions?, back?})` is
 * `EditPage` behind one gate: a reader without `settings.can_view` is told so
 * instead of being shown a page every request on which would be refused. The
 * props are the interface and nothing else here is.
 *
 * **Saving is the edit bar's.** A page that edits puts `EditBar` last among its
 * children and `useLeaveGuard(dirty)` anywhere, both from `components/edit`.
 * The floating *Save changes* pill (`SaveBar`) and the `useUnsavedGuard` alias
 * that stood here until Wave 14E are gone: every caller moved onto the bar
 * (T-4.67), and a second way to save a page is what ADR-0032 §5 retired.
 */

import { EmptyState } from '@fiveox/ui'
import type { ReactNode } from 'react'

import { EditPage } from '../../../components/edit'
import { SETTINGS, useEvaluation } from '../../../lib/access'

export interface SettingsPageProps {
  title: string
  description: string
  children: ReactNode
  /** The page's own actions — one primary at most (Wave 12's UI rule), and
   *  never a Save: that is the edit bar's. */
  actions?: ReactNode
  /** Where the back link goes. Settings by default; the pages Wave 14 moved
   *  under the website (C13) go back to *Your website*. */
  back?: { to: string; label: string }
}

export function SettingsPage({
  title,
  description,
  children,
  actions,
  back = { to: '/settings', label: 'Settings' },
}: SettingsPageProps) {
  const { can, evaluation } = useEvaluation()

  if (evaluation && !can(SETTINGS, 'can_view')) {
    return (
      <EmptyState
        title="Settings aren’t open to you"
        description="Ask a partner or your practice manager — they can change the firm’s settings, and give you access if you need it."
      />
    )
  }

  return (
    <EditPage title={title} description={description} back={back} actions={actions}>
      {children}
    </EditPage>
  )
}

/** A section of a settings page: a heading with an icon, one line of what it
 *  is for, and a body. Drawn as a card so a long page reads as a set of
 *  answerable questions rather than one long form. */
export function SettingsSection({
  id,
  icon,
  title,
  description,
  children,
  footer,
}: {
  id?: string
  icon?: ReactNode
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      className="border-border scroll-mt-24 rounded-2xl border bg-card shadow-sm"
    >
      <header className="border-border flex items-start gap-3 border-b px-5 py-4 sm:px-6">
        {icon && (
          <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <h2 id={id ? `${id}-title` : undefined} className="text-foreground text-base font-semibold">
            {title}
          </h2>
          {description && <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>}
        </div>
      </header>
      <div className="px-5 py-5 sm:px-6">{children}</div>
      {footer && <footer className="border-border border-t px-5 py-3 sm:px-6">{footer}</footer>}
    </section>
  )
}
